import { useState } from "react"
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { Pagination } from "./Pagination"
import Modal from "@/components/Modal"
import FormHeader from "@/components/FormHeader"
import { MdSync } from "react-icons/md"
import { Loader2 } from "lucide-react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import toast from "react-hot-toast"
import api from "@/lib/axios"
import { AxiosError } from "axios"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
}

const postData = async (jenis: string) => {
  const response = await api.post("/master/sdm/sync", {
    jenis: jenis.toLowerCase(),
  })
  return response.data
}

export function DataTable<TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})

  const [showSync, setShowSync] = useState(false)
  const [selectedJenisSDM, setSelectedJenisSDM] = useState("")

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: postData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listSDM"] })
      setShowSync(false)
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSyncSDM = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(selectedJenisSDM)
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 py-4">
        <Input
          placeholder="Filter by nama lengkap..."
          value={table.getColumn("nama_lengkap")?.getFilterValue() as string}
          onChange={(event) =>
            table.getColumn("nama_lengkap")?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
        <div className="flex gap-x-3">
          <Button
            className="flex items-center gap-x-3"
            onClick={() => setShowSync(true)}
          >
            Sinkronisasi
            <MdSync />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="border rounded-xl">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <br />
      <div>
        <Pagination table={table} />
      </div>

      <Modal
        show={showSync}
        onClose={() => setShowSync(false)}
        closeable={!isPending}
      >
        <form onSubmit={handleSyncSDM} className="flex flex-col gap-y-3">
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data SDM"
            description={`Apakah anda yakin akan melakukan sinkronisasi data SDM?`}
          />
          <div>
            <Label>Jenis SDM</Label>
            <Select
              value={selectedJenisSDM}
              onValueChange={(value) => {
                setSelectedJenisSDM(value)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={"Pilih Jenis SDM"}>
                  {selectedJenisSDM &&
                    ["Dosen", "Pegawai"]?.find(
                      (value) => value === selectedJenisSDM
                    )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {["Dosen", "Pegawai"]?.map((value, index) => (
                  <SelectItem key={index} value={`${value}`}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPending}
              onClick={() => setShowSync(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
